import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from '@angular/core';
import { AppConfigService } from "@app/services/app-config.service";
import { Observable, catchError, of, tap } from "rxjs";

export const GENERATE_MOVEMENT_EXECUTION_STATE_KEY = 'generateMovementExecutionState';

export interface GenerativeMovementRequest {
  caseId: string;
  ruleTag: string;
  startRange: string | Date;
  endRange: string | Date;
  executedByUser: string;
}

export type MovementRuleTag = string;

export interface MovementGenerationRule {
  ruleTag: string;
  displayName: string;
  displayIndex: number;
}

export interface MovementGenerationRulesForPlanValue {
  rules: MovementGenerationRule[];
  startDate: string;
  endDate: string;
}

export interface MovementGenerationRulesForPlanResponse {
  value: MovementGenerationRulesForPlanValue;
  isSuccess: boolean;
  message?: unknown;
  hasErrors?: boolean;
  hasWarning?: boolean;
  hasMessages?: boolean;
}

export interface MovementExecutionStartResponse {
  id: string;
  statusQueryGetUri: string;
  sendEventPostUri?: string;
  terminatePostUri?: string;
  purgeHistoryDeleteUri?: string;
  rewindPostUri?: string;
  suspendPostUri?: string;
  resumePostUri?: string;
}

export interface MovementExecutionStatusResponse {
  runtimeStatus?: string;
  output?: {
    success?: boolean;
    message?: string;
    logEntries?: Array<{ actionType?: string; action?: string }>;
  };
}

export interface PersistedMovementExecutionState {
  planId: string;
  region: string;
  statusQueryGetUri: string;
  executionId: string;
  startedAtUtc: string;
}

@Injectable({
  providedIn: 'root'
})
export class MovementGenRulesService {

  constructor() { }
   private readonly httpClient = inject(HttpClient);
    private apiUrl = AppConfigService.settings.apiUrl;
    private readonly rulesCache = new Map<string, { expiresAt: number; data: MovementGenerationRulesForPlanResponse }>();
    private static readonly RULES_CACHE_TTL_MS = 45_000;
  
    private getRegion(): string {
      return localStorage.getItem('selectedRegion') || '';
    }

    /**
     * Fetch movement generation rules for a specific plan and region.
     * API: GET api/generate-movement/rules/{planId}/{region}
     */
    getMovementGenerationRulesForPlan(
      planId: string,
      region: string = this.getRegion()
    ): Observable<MovementGenerationRulesForPlanResponse | null> {
      if (!planId) {
        console.error('PlanId is required. Cannot fetch movement generation rules.');
        return of(null);
      }

      if (!region) {
        console.error('Region is not set. Cannot fetch movement generation rules.');
        return of(null);
      }

      const cacheKey = `${region}:${planId}`;
      const cachedEntry = this.rulesCache.get(cacheKey);
      if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
        return of(cachedEntry.data);
      }

      const fullUrl = `${this.apiUrl}/generate-movement/rules/${encodeURIComponent(
        planId
      )}/${encodeURIComponent(region)}`;
      const params = new HttpParams().set('preventLoader', 'true');

      return this.httpClient.get<MovementGenerationRulesForPlanResponse>(fullUrl, { params }).pipe(
        tap((response) => {
          if (response?.value) {
            this.rulesCache.set(cacheKey, {
              expiresAt: Date.now() + MovementGenRulesService.RULES_CACHE_TTL_MS,
              data: response
            });
          }
        }),
        catchError((error) => {
          console.error('Error fetching movement generation rules', error);
          console.error('Request URL was:', fullUrl);
          return of(null);
        })
      );
    }

    getMovementGenerationRules(): Observable<MovementRuleTag[] | null> {
      const region = this.getRegion();

      if (!region) {
        console.error("Region is not set. Cannot fetch movement generation rules.");
        return of(null);
      }

      const fullUrl = `${this.apiUrl}/generate-movement/rules/${region}`;

      return this.httpClient.get<MovementRuleTag[]>(fullUrl).pipe(
        catchError(error => {
          console.error("Error fetching movement generation rules", error);
          console.error("Request URL was:", fullUrl);
          return of(null);
        })
      );
    }

    executeMovementGeneration(payload: GenerativeMovementRequest): Observable<any> {
      const region = this.getRegion();

      if (!region) {
        console.error("Region is not set. Cannot execute movement generation.");
        return of(null);
      }

      const fullUrl = `${this.apiUrl}/generate-movement/execute/${region}`;
      const params = new HttpParams().set('preventLoader', 'true');

      return this.httpClient.post(fullUrl, payload, { params }).pipe(
        catchError(error => {
          console.error("Error executing movement generation", error);
          console.error("Request URL was:", fullUrl);
          return of(null);
        })
      );
    }

    startMovementGenerationAsync(payload: GenerativeMovementRequest): Observable<MovementExecutionStartResponse | null> {
      const region = this.getRegion();

      if (!region) {
        console.error('Region is not set. Cannot start async movement generation.');
        return of(null);
      }

      const fullUrl = `${this.apiUrl}/generate-movement/execute-async/${region}`;
      const params = new HttpParams().set('preventLoader', 'true');

      return this.httpClient.post<MovementExecutionStartResponse>(fullUrl, payload, { params }).pipe(
        catchError((error) => {
          console.error('Error starting async movement generation', error);
          console.error('Request URL was:', fullUrl);
          return of(null);
        })
      );
    }

    getMovementGenerationExecutionStatus(statusQueryGetUri: string): Observable<MovementExecutionStatusResponse | null> {
      if (!statusQueryGetUri) {
        return of(null);
      }

      const separator = statusQueryGetUri.includes('?') ? '&' : '?';
      const statusUrl = `${statusQueryGetUri}${separator}preventLoader=true`;

      return this.httpClient.get<MovementExecutionStatusResponse>(statusUrl).pipe(
        catchError((error) => {
          console.error('Error fetching async movement generation status', error);
          console.error('Request URL was:', statusUrl);
          return of(null);
        })
      );
    }

    
}
